import { createEntityRecord } from '@kizen/api/records';
import { useState, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';

import { getDataQAForInput } from 'components/Inputs/helpers';
import { toastVariant, useToast } from 'components/ToastProvider';
import useField from 'hooks/useField';
import { isReasonLostField, isStageField } from 'checks/fields';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import CustomObjectsService from 'services/CustomObjectsService';
import FieldInput from 'components/Fields/FieldInput';
import PipelineService from 'services/PipelineService';
import Loader from 'components/Kizen/Loader';
import { useFieldsQuery, useModelQuery } from 'queries/models/custom-objects';
import BasicModalWithConfirmation from '../presets/BasicModalWithConfirmation';
import { isPipelineObject } from '../utilities';
import {
  UnarchiveConflictModal,
  UnarchiveForbiddenModal,
} from '../UnarchiveModal';
import {
  CONTACT,
  PIPELINE,
  CUSTOM_OBJECT,
  dummyObject,
  updateErrors,
  hasErrors,
  getToastConfigMessage,
  getObjectName,
  getCreateEntityFormValues,
  PERMISSION_DENIED,
  UNARCHIVE_CONFLICT,
  UNARCHIVE_PROMPT,
  UNARCHIVE,
} from './helpers';
import {
  useCanFocusField,
  useKeyListeners,
} from 'hooks/keyboardEventHandler/useKeyListeners';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { getErrorMessage } from 'hooks/useErrors';
import { getPayload } from './helpers';

export { CONTACT, PIPELINE, CUSTOM_OBJECT };

const EMPTY_OBJECT = {};

// for better understanding how initialValues works please check "FieldService.getFormValues" function
const CreateEntityModalShort = ({
  objectType,
  objectId,
  onCreated,
  onCancel,
  show,
  initialValues = EMPTY_OBJECT,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();

  const [dirty, setDirty] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [errors, setErrors] = useState({});

  const toastMessage = useRef(getToastConfigMessage(objectType, t));
  const anchorRef = useRef(null);
  const [isUnarchiveForbidden, setIsUnarchiveForbidden] = useState(false);
  const [isUnarchiveConflict, setIsUnarchiveConflict] = useState(false);
  const [isUnarchiveMessage, setIsUnarchiveMessage] = useState('');
  useLayoutEffect(() => {
    if (objectType === CONTACT) {
      // special case for contacts. always email field are last field (in short form) and in this case validation message shows incorrectly
      // for handle that - we must to change overflow property for our modal body
      // I didn't found a better way then I have here 🤷‍♂️
      anchorRef.current?.parentElement?.setAttribute(
        'style',
        'overflow-y: visible'
      );
    }
  }, [objectType]);

  const { data: allFields, isLoading: isLoadingFields } = useFieldsQuery(
    { objectType, id: objectId },
    !!objectType && show
  );

  const { data: modelRaw = EMPTY_OBJECT, isLoading: isLoadingModel } =
    useModelQuery(objectId, !!objectId && show);

  const model = useMemo(() => {
    return {
      ...modelRaw,
      access: { ...(modelRaw.access || {}), edit: modelRaw.entityAccess },
    };
  }, [modelRaw]);

  const fields = useMemo(
    () =>
      (allFields || [])
        .filter((f) => f.includeInShortForm || isReasonLostField(f))
        .sort((a, b) => parseInt(a.order, 10) - parseInt(b.order, 10)),
    [allFields]
  );

  const currentName = useMemo(
    () => getObjectName(model, objectType),
    [model, objectType]
  );

  const serviceToUse = useMemo(() => {
    if (!model) return null;
    return isPipelineObject(model) ? PipelineService : CustomObjectsService;
  }, [model]);

  const [values, setValues] = useField(() => {
    if (objectType === CONTACT) {
      return getCreateEntityFormValues(
        { ...dummyObject, ...initialValues },
        fields
      );
    }

    if (!model) return {};

    const { name, owner, created, ...rest } = model; // exclude name, owner and created from CO
    const reasonLostField = fields.find(isReasonLostField);
    const reasonLostFieldData = reasonLostField
      ? { ...reasonLostField, value: [] }
      : null;
    return getCreateEntityFormValues(
      { ...rest, ...initialValues, fields: [reasonLostFieldData] },
      fields
    );
  }, [fields, model, show]);

  const canFocusField = useCanFocusField(
    objectType === CONTACT ? dummyObject : model
  );

  const { assignFieldHandle, getKeyListenersProps, resetFocus } =
    useKeyListeners(fields, { canFocusField });

  const handleClose = useCallback(() => {
    setErrors({});
    setValues({});
    onCancel?.();
    setDirty(false);
    resetFocus();
  }, [onCancel, resetFocus, setValues]);

  const handleChangeField = useCallback(
    (val, field) => {
      setDirty(true);
      setErrors(updateErrors(errors, field));
      let oldValues = cloneDeep(values);

      if (isStageField(field)) {
        const stage = field.options.find((opt) => opt.id === val);
        const stageDetails = model?.pipeline?.stages?.find(
          (s) => s.id === stage.id
        );
        if (stageDetails?.status !== 'lost') {
          const reasonLostField = fields.find(isReasonLostField);
          if (reasonLostField) {
            oldValues = { ...oldValues, [reasonLostField.id]: [] };
          }
        }
      }

      setValues({ ...oldValues, [field.id]: val });
    },
    [errors, values, setValues, fields, model]
  );

  const handleSubmit = useCallback(
    async (unarchive = UNARCHIVE_PROMPT) => {
      setDisableSubmit(true);

      const errors = hasErrors(fields, values, t);
      if (Object.keys(errors).length !== 0) {
        setDisableSubmit(false);
        setErrors(errors);
        return;
      }

      try {
        const valuesForPayload = fields.reduce((acc, field) => {
          if (field.id in values && field.access.edit) {
            acc[field.id] = values[field.id];
          }
          return acc;
        }, {});
        const payload = getPayload(valuesForPayload);

        const data = await createEntityRecord(AxiosService, objectId, {
          fields: payload,
          unarchive,
        });

        await onCreated(data);
        showToast(
          unarchive === UNARCHIVE
            ? {
                variant: toastVariant.SUCCESS,
                message: `${t(`EntityName was successfully unarchived`, {
                  entityName: currentName,
                })}`,
              }
            : toastMessage.current.success
        );
        handleClose();
      } catch (error) {
        const orig = getOriginalError(error);
        if (orig) {
          if (orig.code === PERMISSION_DENIED) {
            setIsUnarchiveForbidden(true);
            setIsUnarchiveMessage(orig.detail);
          }
          if (orig.code === UNARCHIVE_CONFLICT) {
            setIsUnarchiveConflict(true);
            setIsUnarchiveMessage(orig.detail);
          }

          // special error status
          if (orig.non_field_errors) {
            showToast({
              ...toastMessage.current.error,
              message: orig.non_field_errors.toString(),
            });
          } else {
            const requestPayload = JSON.parse(error.config?.data || '{}');

            const errorFields = orig.fields
              ? orig.fields.reduce((acc, item, i) => {
                  const message = getErrorMessage(item);
                  const fieldId = requestPayload.fields?.[i]?.id;
                  if (message && fieldId) {
                    acc[fieldId] = { message };
                  }
                  return acc;
                }, {})
              : Object.keys(orig).reduce((acc, item) => {
                  const f = fields.find((i) => item === i.name);
                  const message = getErrorMessage(orig[item]);
                  if (f?.id) {
                    acc[f.id] = { message };
                  }
                  return acc;
                }, {});

            setErrors(errorFields);
          }
        } else {
          // rest unhandled cases
          showToast(toastMessage.current.error);
        }
      } finally {
        setDisableSubmit(false);
      }
    },
    [
      currentName,
      fields,
      handleClose,
      objectId,
      onCreated,
      showToast,
      t,
      values,
    ]
  );

  const handleUnarchive = useCallback(
    (unarchive) => {
      handleSubmit(unarchive);
      setIsUnarchiveConflict(false);
      setIsUnarchiveMessage('');
    },
    [handleSubmit]
  );

  const fieldErrors = useMemo(() => {
    let hasFirstError = false;
    return fields.reduce((acc, field) => {
      if (errors[field.id]) {
        acc[field.id] = hasFirstError ? true : errors[field.id];
        hasFirstError = true;
      }
      return acc;
    }, {});
  }, [errors, fields]);

  return (
    <BasicModalWithConfirmation
      dirty={dirty}
      className={`no-drag`}
      show={!!objectType && show}
      onHide={handleClose}
      onConfirm={handleSubmit}
      heading={
        objectType === CONTACT
          ? t(`Add Contact`)
          : `${t('Add')} ${model?.entityName || ''}`
      }
      disabled={disableSubmit}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      hasOneBtnSubmit
      enforceFocus={false}
    >
      <KeyBoardContext.Provider value={{ assignFieldHandle }}>
        <Loader loading={isLoadingFields || isLoadingModel}>
          <UnarchiveForbiddenModal
            show={isUnarchiveForbidden}
            onConfirm={() => {
              setIsUnarchiveForbidden(false);
              setIsUnarchiveMessage('');
            }}
            onHide={() => {
              setIsUnarchiveForbidden(false);
              setIsUnarchiveMessage('');
            }}
            message={isUnarchiveMessage}
          />
          <UnarchiveConflictModal
            show={isUnarchiveConflict}
            onConfirm={() => handleUnarchive(UNARCHIVE)}
            onAdditionalConfirm={() => handleUnarchive('overwrite')}
            onHide={() => {
              setIsUnarchiveConflict(false);
              setIsUnarchiveMessage('');
            }}
            message={isUnarchiveMessage}
          />

          {fields?.map((field) => {
            return (
              <div className={`span${field.meta.cols || 1}`} key={field.id}>
                <FieldInput
                  margin
                  variant="outline"
                  field={field}
                  access={
                    objectType === CONTACT ? dummyObject.access : model?.access
                  }
                  value={values[field.id]}
                  onChange={(val) => handleChangeField(val, field)}
                  isGetFullObject // to ensure the whole fileObject is returned not just the id
                  menuInline
                  error={fieldErrors[field.id] || null}
                  serviceToUse={serviceToUse}
                  objectId={objectId}
                  {...getDataQAForInput(
                    `short-form-${field.name}`,
                    field.fieldType
                  )}
                  {...getKeyListenersProps(field.id)}
                />
              </div>
            );
          })}
        </Loader>
      </KeyBoardContext.Provider>
      <div ref={anchorRef} />
    </BasicModalWithConfirmation>
  );
};

export default CreateEntityModalShort;
